import { useMutation } from '@tanstack/react-query';
import { useForm, useWatch } from 'react-hook-form';

import Logger from 'browser-logging/src/browser/Logger';

import {
  getPasswordRules,
  isPasswordValid,
} from 'common/src/validators/passwordValidation';

import useAuth from 'portal/src/auth/useAuth';
import { FormContainer } from 'portal/src/components/common/forms/FormContainer';
import PasswordRulesDisplay from 'portal/src/components/common/PasswordRulesDisplay';
import { AccountProviderIds } from 'portal/src/constants/AccountProviderIds';
import { MESSAGES } from 'portal/src/constants/snackbarMessages';
import useSnackbarHandler from 'portal/src/utils/Snackbar/useSnackbarHandler';

import { getFields } from './ChangePasswordForm.fields';

type ChangePwFormValues = {
  newPassword: string;
  confirmPassword: string;
};

const ChangePasswordForm = ({
  onCancel,
  onSaveChange,
  providerId,
}: {
  onCancel: () => void;
  onSaveChange: () => void;
  providerId: string;
}) => {
  const snackbarHandler = useSnackbarHandler();
  const { changePassword, getUser, signup } = useAuth();

  const {
    control,
    handleSubmit,
    formState: {
      isDirty,
      dirtyFields: {
        newPassword: newPwIsDirty,
        confirmPassword: confirmPwIsDirty,
      },
    },
  } = useForm({
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
      currentPassword: '',
    },
  });
  const [newPassword, confirmPassword] = useWatch({
    control,
    name: ['newPassword', 'confirmPassword'],
  });
  const { valid, results: constraints } = isPasswordValid(
    newPassword,
    confirmPassword,
    getPasswordRules(getUser()?.organization),
  );
  const isSubmitDisabled = !valid;
  const formFieldsConfig = getFields();

  const { mutate: tryChangePassword, isPending } = useMutation({
    mutationFn: async ({ newPassword }: ChangePwFormValues) => {
      if (providerId === AccountProviderIds.MICROSOFT) {
        await signup(getUser()?.id, newPassword);
      } else {
        await changePassword(newPassword);
      }
    },
    onSuccess: () => {
      snackbarHandler.success(MESSAGES.PASSWORD.CHANGED);
      onSaveChange();
    },
    onError: (err) => {
      Logger.captureError(err as Error);
      snackbarHandler.error(MESSAGES.PASSWORD.CHANGE_ERROR);
    },
  });

  const submitChangePassword = (data: ChangePwFormValues) => {
    if (!valid) return;

    tryChangePassword(data);
  };

  return (
    <FormContainer
      beforeMainFields={
        <PasswordRulesDisplay
          constraints={constraints}
          fieldsAreDirty={newPwIsDirty || confirmPwIsDirty}
        />
      }
      control={control}
      handleBackAction={onCancel}
      allowReadOnlyEdit
      handleSubmit={handleSubmit(submitChangePassword)}
      header="Change Password"
      isDirty={isDirty}
      isNew={false}
      isPending={isPending}
      isSubmitDisabled={isSubmitDisabled}
      mainFieldsConfig={formFieldsConfig}
    />
  );
};

export default ChangePasswordForm;
